import React from 'react'
import {
  compose,
  withStateHandlers,
} from 'recompose'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import IconMenu from 'material-ui/IconMenu'
import MenuItem from 'material-ui/MenuItem'
import IconButton from 'material-ui/IconButton'
import RaisedButton from 'material-ui/RaisedButton'
import MoreVertIcon from 'material-ui/svg-icons/navigation/more-vert'
import ModeEdit from 'material-ui/svg-icons/editor/mode-edit'
import DeleteForever from 'material-ui/svg-icons/action/delete-forever'
import { red500 } from 'material-ui/styles/colors'

const Main = styled.div`
  background: #ffffff;
  padding: 20px 30px;
  position: relative;
  z-index: 1100;
  font-family: roboto;
  cursor: ${({ sortable }) => sortable ? 'move' : 'initial'};
  border-bottom: ${({ dividing }) => dividing ? '1px solid #d5d5d5' : 'none'};
  
  .more-actions {
    position: absolute !important;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
  }
`

const Div = styled.div`
  text-align: right;
  padding-top: 1rem;
  
  div:not(:last-child) {
    margin-right: 20px;
  }
`

const ItemUpdater = ({
  showActions,
  actionsDisabled,
  onEdit,
  onDelete,
  onDiscard,
  onSave,
  isSaving,
  children,
  sortable,
  dividing,
}) => {
  return (
    <Main dividing={dividing} sortable={sortable}>
      {children}
      {(!showActions && !actionsDisabled) && (
        <IconMenu
          className={'more-actions'}
          iconButtonElement={(
            <IconButton>
              <MoreVertIcon />
            </IconButton>
          )}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
          targetOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
        >
          <MenuItem
            leftIcon={<ModeEdit />}
            primaryText="Edit"
            onClick={onEdit}
          />
          <MenuItem
            leftIcon={<DeleteForever color={red500} />}
            primaryText="Delete"
            onClick={onDelete}
          />
        </IconMenu>
      )}
      {showActions && (
        <Div>
          <RaisedButton
            label={'Discard'}
            primary
            disabled={isSaving}
            onTouchTap={onDiscard}
          />
          <RaisedButton
            label={isSaving ? 'Saving...' : 'Save'}
            disabled={isSaving}
            secondary
            onTouchTap={onSave}
          />
        </Div>
      )}
    </Main>
  )
}

ItemUpdater.propTypes = {
  children: PropTypes.node,
  showActions: PropTypes.bool,
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  actionsDisabled: PropTypes.bool,
  isSaving: PropTypes.bool,
  sortable: PropTypes.bool,
  dividing: PropTypes.bool,
}
ItemUpdater.defaultProps = {
  children: null,
  showActions: false,
  actionsDisabled: false,
  sortable: false,
  isSaving: false,
  dividing: false,
  onDiscard: () => {},
  onSave: () => {},
  onEdit: () => {},
  onDelete: () => {},
}
ItemUpdater.Enhance = compose(
  withStateHandlers(
    {
      editingState: false,
      temporaryValue: null,
    },
    {
      setEditingState: ({ editingState }) => () => ({ editingState: !editingState }),
      onTempValueChange: () => (temporaryValue) => ({ temporaryValue }),
    },
  ),
  (WrappedComponent) => {
    const Enhanced = ({
      dividing,
      sortable,
      value,
      onEdit,
      onDelete,
      onDiscard,
      onSave,
      isSaving,
      actionsDisabled,
      editingState,
      setEditingState,
      temporaryValue,
      onTempValueChange,
      ...props
    }) => {
      const handleDiscard = () => {
        onDiscard()
        // change editing state to false
        setEditingState()
      }
      return (
        <ItemUpdater
          onEdit={() => {
            // call onEdit from parent
            onEdit()
            // change editing state to true
            setEditingState()
            // when click edit, save value to temporary state
            onTempValueChange(value)
          }}
          sortable={sortable}
          dividing={dividing}
          showActions={editingState}
          actionsDisabled={actionsDisabled}
          onDelete={onDelete}
          onDiscard={handleDiscard}
          onSave={() => {
            onSave(temporaryValue).then((result) => {
              setEditingState()
              return result
            })
          }}
          isSaving={isSaving}
        >
          <WrappedComponent
            // in editing state, will use temporary value
            isEditing={editingState}
            value={editingState ? temporaryValue : value}
            onChange={onTempValueChange}
            {...props}
          />
        </ItemUpdater>
      )
    }
    Enhanced.propTypes = {
      ...ItemUpdater.propTypes,
    }
    Enhanced.defaultProps = {
      ...ItemUpdater.defaultProps,
    }
    return Enhanced
  },
)

export default ItemUpdater



// WEBPACK FOOTER //
// ./src/components/wrappers/ItemUpdater.js